import { Button, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material';
import { cancelButtonStyle, saveButtonStyle } from '../../style';
import { ReviewExtendReasonDto } from '../../lib/dtos/ReviewDtos';
import { ChangeEventHandler, FormEventHandler, useEffect, useState } from 'react';
import { EMPTY_GUID } from '../../lib/constants/misc';
import { AxiosError } from 'axios';
import { toast, ToastContentProps } from 'react-toastify';
import { ErrorResponse } from '../../lib/dtos/ErrorResponse';
import { useScreenLargerThan } from '../../lib/hooks/breakpoint-hooks';
import { StyledDialogActions } from '../../styledComponents';

export type ReviewExtendReasonModalProps = {
    reviewExtendReason: ReviewExtendReasonDto | null;
    open: boolean;
    onDialogClose: () => void;
    onSubmit: (formData: ReviewExtendReasonDto) => Promise<void>;
};

const ReviewExtendReasonModal = ({
    reviewExtendReason,
    open,
    onDialogClose,
    onSubmit
}: ReviewExtendReasonModalProps) => {
    const isAdd = !reviewExtendReason?.id;

    const [reason, setReason] = useState('');
    const [isErrorHelperText, setErrorHelperText] = useState(false);
    const isLargerThanMedium = useScreenLargerThan('md');

    useEffect(() => {
        if (!isAdd) {
            setReason(reviewExtendReason.reason);
            setErrorHelperText(false);
        } else {
            setReason('');
            setErrorHelperText(false);
        }
    }, [reviewExtendReason, isAdd]);

    const handleCloseDialog = () => {
        onDialogClose.call(null);
    };

    const handleFormSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();
        const formData: ReviewExtendReasonDto = reviewExtendReason
            ? { ...reviewExtendReason, reason }
            : { id: EMPTY_GUID, isActive: true, reason };
        await toast.promise(onSubmit.call(null, formData), {
            success: 'Success',
            pending: isAdd ? 'Adding value...' : 'Updating value...',
            error: {
                render: ({ data }: ToastContentProps<AxiosError<ErrorResponse>>) => {
                    return data?.response?.data.message;
                }
            }
        });
        handleCloseDialog();
    };

    const reasonChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
        setErrorHelperText(false);
        if (/^[A-Za-z0-9\s`!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>/?~]*$/.test(e.target.value)) {
            setReason(e.target.value);
        } else {
            setErrorHelperText(true);
        }
    };

    return (
        <Dialog open={open} onClose={handleCloseDialog}>
            <DialogTitle variant="h1">{isAdd ? 'Add' : 'Edit'} Review Extend Reason</DialogTitle>
            <form onSubmit={handleFormSubmit}>
                <DialogContent>
                    <TextField
                        sx={{ width: isLargerThanMedium ? 400 : 260 }}
                        size="small"
                        label="Reason"
                        required
                        value={reason}
                        onChange={reasonChangeHandler}
                    />
                </DialogContent>
                <StyledDialogActions>
                    <Button sx={cancelButtonStyle} onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button sx={saveButtonStyle} type="submit" disabled={isErrorHelperText}>
                        Save
                    </Button>
                </StyledDialogActions>
            </form>
        </Dialog>
    );
};

export default ReviewExtendReasonModal;
